import { frameId } from "../assets/manifest.ts";
import { EMPTY_TILE, unpackTile } from "../document/packed-tile.ts";
import type { PropBehaviour, TileRef, WorldDocument } from "../document/types.ts";
import type { CompileContext } from "./context.ts";
import { resolveTileLayers } from "./resolve-map.ts";

/**
 * The frames a view of a document draws (ADR-130 decision 2.6), computed from the document and the context with no
 * compile of the chunks: what a tab asks the library for at load and again on a reconnect. It is written to contain
 * every frame `framesUsedBy` reads out of the compiled world, so on-demand loading never lacks one the compile draws.
 */

/**
 * The part of a compile context the frames of a document need: the terrains and rule sets a terrain layer resolves
 * through, and the one-shot strips a transition animation expands to.
 */
export type PlacedFramesContext = Pick<CompileContext, "terrains" | "ruleSets" | "transitionStrips">;

/**
 * Adds the frames of a placed prop: its own, each state frame its behaviour names, and every frame of the strip each
 * of its behaviour's animations starts. The compile's appearance table holds the states of the prop's type and the
 * strips of the transitions the type declares, which are all drawn from these, so this reads the behaviour and not
 * the type and cannot fail on an object type the game has not registered. An animation whose first frame starts no
 * strip in the context adds that frame alone.
 *
 * @param frame The prop's own frame, or none when only the behaviour's frames are wanted.
 * @param behaviour The prop's behaviour, if it has one.
 * @param strips The context's one-shot strips by the frame id of their first frame.
 * @param into The set to add to.
 */
export function addPropFrames(frame: TileRef | undefined, behaviour: PropBehaviour | undefined, strips: NonNullable<PlacedFramesContext["transitionStrips"]>, into: Set<string>): void {
  const add = (ref: TileRef): void => {
    into.add(frameId(ref.tileset, null, ref.tile));
  };
  if (frame !== undefined) {
    add(frame);
  }
  for (const ref of Object.values(behaviour?.frames ?? {})) {
    add(ref);
  }
  for (const ref of Object.values(behaviour?.animations ?? {})) {
    add(ref);
    for (const stripFrame of strips[frameId(ref.tileset, null, ref.tile)]?.frames ?? []) {
      add(stripFrame);
    }
  }
}

/**
 * Lists the frame ids a view of a document draws: each prop's frame, its behaviour's state frames and the strips of
 * its animations (the art the compile's object appearance table draws that no prop's own frame names, ADR-130 H8),
 * and the distinct frames of the tile layers' cells, a terrain layer's after the resolver has chosen its tiles, in the
 * layer's variant. A map that is a draft, or holds no layer, is read like any other, so the set can only be larger
 * than a publish compile's.
 *
 * @param document The world document.
 * @param context The compile context.
 * @returns Every frame id once, in code-unit order.
 * @throws Error when a terrain, tileset or rule set a terrain layer needs is missing from the context or the world,
 *   or a tile cell names a tileset index past the world's list.
 */
export function placedFrames(document: WorldDocument, context: PlacedFramesContext): string[] {
  const frames = new Set<string>();
  const strips = context.transitionStrips ?? {};
  const compileContext = { ...context, entityTypes: [], objectTypes: [], collision: { rules: [], outside: 0 } };
  for (const map of document.maps) {
    for (const layer of map.layers) {
      if (layer.kind === "prop") {
        for (const prop of layer.props) {
          addPropFrames(prop.frame, prop.behaviour, strips, frames);
        }
      }
    }
    for (const layer of resolveTileLayers(document, map, compileContext).layers) {
      const seen = new Set<number>();
      for (const packed of layer.tiles) {
        if (packed === EMPTY_TILE || seen.has(packed)) {
          continue;
        }
        seen.add(packed);
        const { tilesetIndex, tile } = unpackTile(packed);
        const tileset = document.tilesets[tilesetIndex];
        if (tileset === undefined) {
          throw new Error(`layer ${layer.id} of map ${map.id} names tileset index ${tilesetIndex}, but the world lists ${document.tilesets.length} tilesets`);
        }
        frames.add(frameId(tileset.id, layer.variant, tile));
      }
    }
  }
  return [...frames].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
}
